import React, { useCallback } from 'react'
import styles from './uploader.styles.jcss'
import useUpload from './useUpload'
import translate from 'core/translate'
import { cn } from 'ethcss'
import { grid } from 'theme'
import EncodingDropdown from './components/EncodingDropdown'
import CsvDivider from './components/CsvDivider'
import FileItem from './components/FileItem'
import FileInput from './components/FileInput'
import SampleSection from './components/SampleSection'
import Button from 'blocks.simple/button/button'
import { ApiPostEventsType } from 'core/api/events'
import Dropdown from '../../blocks.simple/dropdown/dropdown'
import { ItemData } from '../../core/models/Template'

type Props = {
    uploadMethod: ApiPostEventsType | null
    isModal: boolean
    onClose?: () => void
    headers?: { [key: string]: string }
    customBlock?: any
    showCloseButton?: boolean
    onResponse?: (response: any) => void
    isSelectFromFile?: boolean
    selectedItems?: ItemData[]
}

const Uploader = ({
    uploadMethod,
    isModal,
    onClose,
    headers,
    showCloseButton,
    onResponse,
    isSelectFromFile,
    selectedItems,
    customBlock,
}: Props) => {
    const handleUploadSuccess = useCallback(
        (response: { body: unknown }) => {
            onResponse?.(response)
            if (isModal) {
                onClose?.()
            }
        },
        [isModal, onClose, onResponse]
    )

    const {
        addFiles,
        uploadRef,
        deleteFile,
        encoding,
        handleSelectEncoding,
        handleSubmit,
        divider,
        setDivider,
        files,
        acceptFiles,
        actionType,
        uploadTypeValue,
        setUploadTypeValue,
    } = useUpload({
        uploadMethod,
        headers,
        onSuccess: onResponse || isModal ? handleUploadSuccess : undefined,
        isSelectFromFile,
        selectedItems,
    })

    return (
        <div className={cn(styles.wrapper, !isModal ? styles.wrapperShadow : '')}>
            <div className={styles.uploadLabel}>{translate('uploadFiles')}</div>
            <div className={cn(grid.mt_mini, grid.mb_mini)}>{translate('uploadFilesSubtitle')}</div>
            <SampleSection actionType={actionType} isCsv={acceptFiles === '.csv'} />
            {customBlock && customBlock()}
            {files.length === 0 && (
                <FileInput isCsv={acceptFiles === '.csv'} uploadRef={uploadRef} addFiles={addFiles} />
            )}
            {files.map((file) => (
                <FileItem file={file} deleteFile={deleteFile} />
            ))}
            <div className={styles.uploadOptions}>
                <CsvDivider showDivider={acceptFiles === '.csv'} divider={divider} setDivider={setDivider} />
                <EncodingDropdown
                    handleSelectEncoding={handleSelectEncoding}
                    showEncoding={acceptFiles === '.csv'}
                    encoding={encoding}
                />
            </div>
            {isSelectFromFile && (
                <div className={styles.dropdown}>
                    <Dropdown
                        className={styles.orderModeDropdown}
                        value={uploadTypeValue}
                        options={[
                            {
                                name: translate('addToSelected'),
                                id: 'addToSelected',
                            },
                            {
                                name: translate('replaceSelected'),
                                id: 'replaceSelected',
                            },
                        ]}
                        label={translate('uploadType')}
                        onChange={(selected) => {
                            setUploadTypeValue(selected.id)
                        }}
                    />
                </div>
            )}
            <div className={styles.buttons}>
                {(isModal || showCloseButton) && (
                    <Button className={cn(grid.mt_miniPlus)} mod={'default'} onClick={onClose}>
                        {translate('cancel')}
                    </Button>
                )}
                <Button
                    className={cn(grid.mt_miniPlus)}
                    mod={'fill'}
                    disabled={files.length === 0}
                    onClick={handleSubmit}
                >
                    {translate('upload')}
                </Button>
            </div>
        </div>
    )
}

export default Uploader
